/**
 * Component for wrapping a video track with measurement capabilities
 */
import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { View } from '@smarterservices/smarter-ui';
import VideoTrack from './VideoTrack';
import OfflineTrackPlaceholder from './OfflineTrackPlaceholder';
import { useLogger } from '@ui/shared/components/Logger';
import { BACKGROUND_COLOR, CANVAS_WIDTH, CANVAS_HEIGHT, LOGGER_LABEL } from '../utils/constants';

/**
 * Timeout in milliseconds before attaching the ResizeObserver
 * This gives the video time to load and stabilize its dimensions
 */
const RESIZE_OBSERVER_TIMEOUT = 500;

/**
 * TrackWrapper component wraps a video track and measures its position
 *
 * @param {object} props - Component props
 * @param {string} props.trackSid - The track SID
 * @param {object} props.trackRef - The LiveKit track reference
 * @param {function} props.onPositionUpdate - Callback for position updates
 * @returns {React.ReactElement} - The rendered component
 */
const TrackWrapper = ({ trackSid, trackRef, onPositionUpdate }) => {
  const logger = useLogger();
  
  // Reference to the video element container for position measurement
  const containerRef = useRef(null);
  // Reference to the actual video element for measurement
  const videoRef = useRef(null);
  // Reference to the ResizeObserver instance
  const resizeObserverRef = useRef(null);
  // Reference to track video dimension changes
  const lastDimensionsRef = useRef({ width: 0, height: 0 });
  // Reference to the dimension check interval
  const dimensionCheckIntervalRef = useRef(null);
  
  // Log component mount/unmount
  useEffect(() => {
    logger.debug(LOGGER_LABEL, 'TrackWrapper mounted', {
      trackSid,
      hasTrackRef: !!trackRef
    });
    
    return () => {
      logger.debug(LOGGER_LABEL, 'TrackWrapper unmounted', {
        trackSid
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for video dimension changes (for phone rotation detection)
  const checkVideoDimensions = useCallback(() => {
    try {
      if (videoRef.current && videoRef.current.videoWidth && videoRef.current.videoHeight) {
        const currentWidth = videoRef.current.videoWidth;
        const currentHeight = videoRef.current.videoHeight;
        const lastDimensions = lastDimensionsRef.current;
        
        if (currentWidth !== lastDimensions.width || currentHeight !== lastDimensions.height) {
          logger.info(LOGGER_LABEL, 'Video dimensions changed (rotation detected)', {
            trackSid,
            oldWidth: lastDimensions.width,
            oldHeight: lastDimensions.height,
            newWidth: currentWidth,
            newHeight: currentHeight
          });
          
          lastDimensionsRef.current = { width: currentWidth, height: currentHeight };
          
          // Video dimensions changed, trigger position update
          if (videoRef.current) {
            try {
              const rect = videoRef.current.getBoundingClientRect();
              const positionObject = {
                top: Math.round(rect.top),
                left: Math.round(rect.left),
                width: Math.round(rect.width),
                height: Math.round(rect.height),
              };
              onPositionUpdate(trackSid, trackRef, positionObject);
            } catch (rectError) {
              logger.error(LOGGER_LABEL, 'Error getting bounding rect during dimension check', {
                error: rectError,
                errorMessage: rectError.message,
                trackSid
              });
            }
          }
        }
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error checking video dimensions', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPositionUpdate, trackSid, trackRef]);

  // Callback to update position/size measurement
  const updatePosition = useCallback(() => {
    try {
      if (!videoRef.current) {
        logger.warn(LOGGER_LABEL, 'Cannot update position: video ref not available', {
          trackSid
        });
        return;
      }
      
      const rect = videoRef.current.getBoundingClientRect();

      // remove the resize observer
      if (resizeObserverRef.current) {
        try {
          resizeObserverRef.current.disconnect();
        } catch (disconnectError) {
          logger.error(LOGGER_LABEL, 'Error disconnecting ResizeObserver', {
            error: disconnectError,
            errorMessage: disconnectError.message,
            trackSid
          });
        }
      }
      
      // round the position values, to nearest pixel...
      const positionObject = {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      };

      logger.debug(LOGGER_LABEL, 'Position updated', {
        trackSid,
        position: positionObject
      });

      // update the position
      onPositionUpdate(trackSid, trackRef, positionObject);
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error updating position', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPositionUpdate, trackSid, trackRef]);

  // Called by VideoTrack when the video is loaded and sized
  const handleVideoLoaded = useCallback(() => {
    try {
      logger.debug(LOGGER_LABEL, 'Video loaded, setting up ResizeObserver', {
        trackSid,
        timeout: RESIZE_OBSERVER_TIMEOUT
      });
      
      // Wait before attaching ResizeObserver to allow video to stabilize
      setTimeout(() => {
        try {
          if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
          }
          
          if (videoRef.current) {
            try {
              resizeObserverRef.current = new ResizeObserver(updatePosition);
              resizeObserverRef.current.observe(videoRef.current);
              
              logger.debug(LOGGER_LABEL, 'ResizeObserver attached', {
                trackSid
              });
            } catch (observerError) {
              logger.error(LOGGER_LABEL, 'Failed to create/attach ResizeObserver', {
                error: observerError,
                errorMessage: observerError.message,
                trackSid
              });
            }
            
            // Initialize dimension tracking
            if (videoRef.current.videoWidth && videoRef.current.videoHeight) {
              lastDimensionsRef.current = {
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
              };
              
              logger.debug(LOGGER_LABEL, 'Initial dimensions tracked', {
                trackSid,
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
              });
            }
            
            // Start polling for dimension changes (for phone rotation detection)
            if (dimensionCheckIntervalRef.current) {
              clearInterval(dimensionCheckIntervalRef.current);
            }
            dimensionCheckIntervalRef.current = setInterval(checkVideoDimensions, 250);
            
            logger.debug(LOGGER_LABEL, 'Dimension check interval started', {
              trackSid,
              intervalMs: 250
            });
          } else {
            logger.warn(LOGGER_LABEL, 'Video ref not available after timeout', {
              trackSid
            });
          }
        } catch (timeoutError) {
          logger.error(LOGGER_LABEL, 'Error in ResizeObserver setup timeout', {
            error: timeoutError,
            errorMessage: timeoutError.message,
            trackSid
          });
        }
      }, RESIZE_OBSERVER_TIMEOUT);
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error in handleVideoLoaded', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePosition, checkVideoDimensions, trackSid]);

  // Clean up ResizeObserver and interval on unmount
  useEffect(() => {
    return () => {
      try {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          logger.debug(LOGGER_LABEL, 'ResizeObserver disconnected on cleanup', {
            trackSid
          });
        }
        if (dimensionCheckIntervalRef.current) {
          clearInterval(dimensionCheckIntervalRef.current);
          logger.debug(LOGGER_LABEL, 'Dimension check interval cleared on cleanup', {
            trackSid
          });
        }
      } catch (error) {
        logger.error(LOGGER_LABEL, 'Error during cleanup', {
          error,
          errorMessage: error.message,
          trackSid
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Also update on window resize
  useEffect(() => {
    try {
      window.addEventListener('resize', updatePosition);
      logger.debug(LOGGER_LABEL, 'Window resize listener added', {
        trackSid
      });
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error adding window resize listener', {
        error,
        errorMessage: error.message,
        trackSid
      });
    }
    
    return () => {
      try {
        window.removeEventListener('resize', updatePosition);
        logger.debug(LOGGER_LABEL, 'Window resize listener removed', {
          trackSid
        });
      } catch (error) {
        logger.error(LOGGER_LABEL, 'Error removing window resize listener', {
          error,
          errorMessage: error.message,
          trackSid
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePosition, trackSid]);

  return (
    <View
      as="div"
      width="100%"
      height="100%"
      overflow="hidden"
      position="relative"
      border="none"
      borderRadius="medium"
      background="secondary"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          backgroundColor: BACKGROUND_COLOR,
        }}
      >
        {trackRef ? (
          <VideoTrack
            trackRef={trackRef}
            videoRef={videoRef}
            onVideoLoaded={handleVideoLoaded}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            dataTrackSid={trackSid}
          />
        ) : (
          <OfflineTrackPlaceholder trackSid={trackSid} />
        )}
      </div>
    </View>
  );
};

TrackWrapper.propTypes = {
  trackSid: PropTypes.string.isRequired,
  trackRef: PropTypes.object,
  onPositionUpdate: PropTypes.func,
};

export default TrackWrapper;
